<template>
    <v-sheet>
        <template v-if="hasResult">
            <!-- transit options -->
            <trip-pattern-list :class="{ 'd-none': selectedMode !== GetTripModeDto.Bus }">
                <template v-for="(val, key) in tripPatternGroups" :key="key">
                    <items-group-header v-if="showGroupHeader(key)" :model-value="key" />
                    <trip-pattern-list-item
                        v-for="(tripPattern, i) in val"
                        :key="tripPattern.id"
                        data-id="tripPattern"
                        :data-booking-meta="getBookingMeta(tripPattern)"
                        :model-value="tripPattern"
                        :disabled="tripPattern.disabled"
                        :expanded="tripPattern.id === selectedTripPatternId && selectedTripPatternIdIsExpanded"
                        :is-debug-mode="isDebugMode"
                        :active="tripPattern.id === selectedTripPatternId"
                        :focused="tripPattern.id === selectedTripPatternId || hasBooking(tripPattern) || hasStopOrder(tripPattern)"
                        @click="toggleTripPattern(tripPattern.id, i)"
                        @click:expand="toggleExpanded(tripPattern.id)"
                        @hover="onHover($event, tripPattern)"
                    >
                        <template #post-trip-leg-list>
                            <div class="pt-4">
                                <share-trip-dialog :trip-pattern="tripPattern" />
                            </div>
                        </template>
                    </trip-pattern-list-item>
                </template>
                <template #append>
                    <div v-show="shouldShowLoadMore" v-intersect="onIntersect" class="pa-4 text-center"></div>
                </template>
            </trip-pattern-list>
            <trip-pattern-list :class="{ 'd-none': selectedMode !== GetTripModeDto.Bicycle }">
                <trip-pattern-list-item
                    v-if="bikeSearchResult"
                    data-id="bikeSearchResult"
                    :model-value="bikeSearchResult"
                    :expanded="bikeSearchResult.id === selectedTripPatternId && selectedTripPatternIdIsExpanded"
                    :is-debug-mode="isDebugMode"
                    :active="bikeSearchResult.id === selectedTripPatternId"
                    :now="currentTime"
                    is-in-non-transit-mode
                    hide-leg-duration
                    @click="selectTripPattern(bikeSearchResult.id)"
                    @click:expand="toggleExpanded(bikeSearchResult.id)"
                />
            </trip-pattern-list>
            <trip-pattern-list :class="{ 'd-none': selectedMode !== GetTripModeDto.Foot }">
                <trip-pattern-list-item
                    v-if="footSearchResult"
                    data-id="footSearchResult"
                    :model-value="footSearchResult"
                    :expanded="footSearchResult.id === selectedTripPatternId && selectedTripPatternIdIsExpanded"
                    :is-debug-mode="isDebugMode"
                    :active="footSearchResult.id === selectedTripPatternId"
                    :now="currentTime"
                    is-in-non-transit-mode
                    hide-leg-duration
                    @click="selectTripPattern(footSearchResult.id)"
                    @click:expand="toggleExpanded(footSearchResult.id)"
                />
            </trip-pattern-list>
        </template>

        <v-sheet v-if="isLoading" color="white" class="py-6">
            <v-row no-gutters align="center" justify="center">
                <v-col cols="6">
                    <v-progress-linear
                        data-id="tripSearchLoadingProgress"
                        color="primary"
                        indeterminate
                        rounded
                        height="6"
                        style="left: 0 !important; transform: none !important"
                    />
                </v-col>
            </v-row>
        </v-sheet>
    </v-sheet>
</template>

<script setup lang="ts">
import { useTripSearchStore } from '@/features/trips/stores';
import { TripPatternList, TripPatternListItem, ShareTripDialog } from '@/features/trips/components';
import { computed, nextTick, onBeforeMount, onMounted, provide, watch, watchEffect } from 'vue';
import { storeToRefs } from 'pinia';
import { ItemsGroupHeader } from '@/features/common/components';
import { useDisplay } from 'vuetify';
import { useQueryParams } from './useQueryParams';
import { toLocalDate, isEqual } from '@geta/kolumbus-frontend/composables';
import { parseISO } from 'date-fns';
import { useMapStore } from '@/features/map/stores';
import { defaultPadding } from '@/features/map/composables';
import { SearchMode, useMainStore } from '@/features/common/stores';
import { useSearchPageLayout } from '@/pages/layouts/useSearchPageLayout';
import { isEmpty } from 'lodash-es';
import { useGtm } from 'vue-gtm';
import { useBookingStore } from '@/features/bookings/stores';
import { GetTripModeDto, TripPatternDto } from '@/types/webapi';
import { useBookingFormCard, useBookingCard } from '@/features/bookings/composables';
import { OnlineBookingIsAvailableKey, BookingFormCardProvideKey, BookingCardProvideKey } from '@/injectionKeys';
import { hasValidBooking, hasValidStopOrder } from '@/features/bookings/functions';

const props = defineProps<{ id?: string }>();
const { appInject, searchWrapperInject } = useSearchPageLayout();
const { mobile: isMobile } = useDisplay();
const gtm = useGtm();
const store = useTripSearchStore();
const mainStore = useMainStore();
const { paddingBottomRight } = storeToRefs(useMapStore());
const {
    ready,
    isDirty,
    isLoading,
    hasResult,
    tripPatternGroups,
    selectedTripPatternId,
    selectedTripPatternIdIsExpanded,
    isInNonTransitMode,
    selectedMode,
    bikeSearchResult,
    footSearchResult
} = storeToRefs(store);
const { onlineBookingIsAvailable, bookings, stopOrders } = storeToRefs(useBookingStore());
const { isDebugMode, currentTime } = storeToRefs(mainStore);
const { init: initQueryParams, patchStore: patchQueryParamsStore } = useQueryParams();

provide(OnlineBookingIsAvailableKey, onlineBookingIsAvailable);
provide(BookingFormCardProvideKey, useBookingFormCard());
provide(BookingCardProvideKey, useBookingCard());

function showGroupHeader(key: string | number) {
    if (typeof key !== 'string') return false;

    return !isEqual(toLocalDate(currentTime.value), parseISO(key), 'date');
}

function toggleTripPattern(id: string, index?: number, tripMode?: string) {
    // if showMultipleTripShapes is false, do not allow deselect
    if (selectedTripPatternId.value === id && mainStore.config.searchConfig.showMultipleTripShapes) {
        selectedTripPatternId.value = null;
        selectedTripPatternIdIsExpanded.value = false;
        return;
    }

    selectTripPattern(id, index, tripMode);
}

function selectTripPattern(id: string, index?: number, tripMode?: string) {
    selectedTripPatternId.value = id;

    // track event
    gtm?.trackEvent({
        event: 'app_event',
        category: 'trip_search',
        action: 'trip_item_selected',
        index,
        tripMode
    });
}

function toggleExpanded(id: string) {
    if (!id || !selectedTripPatternId.value) return;

    if (selectedTripPatternId.value === id) {
        selectedTripPatternIdIsExpanded.value = !selectedTripPatternIdIsExpanded.value;

        if (selectedTripPatternIdIsExpanded.value) {
            // track event
            gtm?.trackEvent({
                event: 'app_event',
                category: 'trip_search',
                action: 'trip_details_expanded'
            });
        }
    }
}

const hasBooking = (tripPattern: TripPatternDto) =>
    tripPattern.legs?.some(leg => bookings.value.some(booking => hasValidBooking(booking, leg.bookingMetadata)));
const hasStopOrder = (tripPattern: TripPatternDto) =>
    stopOrders.value.some(stopOrder =>
        hasValidStopOrder(stopOrder, tripPattern.legs?.find(leg => leg.bookingMetadata?.onlineBookingIsAvailable)?.bookingMetadata)
    );
const shouldShowLoadMore = computed(() => !isLoading.value && !isEmpty(tripPatternGroups.value) && !isInNonTransitMode.value);

async function onIntersect(isIntersecting: boolean) {
    if (!isIntersecting || isLoading.value) return;

    await store.searchMore();
}

function getBookingMeta(tripPattern: TripPatternDto): string | undefined {
    const legWithBooking = tripPattern.legs?.find(x => x.bookingMetadata?.onlineBookingIsAvailable);
    if (!legWithBooking) return undefined;

    const { serviceJourneyRef, stopSequence, bookingLimitDateTime } = legWithBooking.bookingMetadata!;
    if (!bookingLimitDateTime || !serviceJourneyRef || !stopSequence) return undefined;

    return `${bookingLimitDateTime.toISOString()}|${serviceJourneyRef}|${stopSequence}`;
}

function onHover(isHovered: boolean, tripPattern: TripPatternDto) {
    // do not highlight shapes if we have a selected trip pattern
    if (selectedTripPatternId.value) return;

    store.highlightShapesForTripPattern(isHovered ? tripPattern : undefined);
}

onBeforeMount(() => {
    mainStore.searchMode = SearchMode.Trip;
});

onMounted(async () => {
    // if id is provided, load trip pattern by id
    if (props.id) {
        await store.loadTripPatternById(props.id);
        await nextTick();

        // expand trip pattern if not mobile
        if (store.searchResult.trip && !isMobile.value) {
            selectedTripPatternIdIsExpanded.value = true;
        }
    }

    initQueryParams();

    if (!props.id) {
        patchQueryParamsStore();
    }

    ready.value = true;
});

// if showMultipleTripShapes is false, select first trip pattern when we have results
watch(
    hasResult,
    () =>
        !isMobile.value &&
        !mainStore.config.searchConfig.showMultipleTripShapes &&
        nextTick(() => {
            // select first item
            store.selectFirstTripPattern();
        })
);

watch(selectedMode, value => {
    if (isMobile.value) return;

    switch (value) {
        case GetTripModeDto.Bicycle:
            selectedTripPatternId.value = bikeSearchResult.value?.id || null;
            break;
        case GetTripModeDto.Foot:
            selectedTripPatternId.value = footSearchResult.value?.id || null;
            break;
        default:
            // if showMultipleTripShapes is false, select first trip pattern
            if (!mainStore.config.searchConfig.showMultipleTripShapes) {
                store.selectFirstTripPattern();
            }
            break;
    }
});

watchEffect(() => {
    searchWrapperInject.setHeaderAppendVisibility(isDirty.value);

    if (!isMobile.value) return;

    searchWrapperInject.setHeaderVisibility(!(selectedTripPatternId.value || selectedTripPatternIdIsExpanded.value));
    appInject.setDrawerFullHeight(!(selectedTripPatternId.value && selectedTripPatternIdIsExpanded.value));

    if (selectedTripPatternId.value) {
        searchWrapperInject.setPage(1);
        paddingBottomRight.value = [defaultPadding, 300];
    } else {
        searchWrapperInject.setPage(0);
        paddingBottomRight.value = [defaultPadding, defaultPadding];
    }
});
</script>
